import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

const THEME_PREFERENCES = ['system', 'light', 'dark'] as const

export type ThemePreference = (typeof THEME_PREFERENCES)[number]

export type ResolvedTheme = Exclude<ThemePreference, 'system'>

const DARK_SCHEME_QUERY = '(prefers-color-scheme: dark)'

function darkSchemeQuery(): MediaQueryList | null {
  return typeof window.matchMedia === 'function' ? window.matchMedia(DARK_SCHEME_QUERY) : null
}

const DEFAULT_THEME: ThemePreference = 'system'
const STORAGE_KEY = 'raldo.theme'

function isThemePreference(value: unknown): value is ThemePreference {
  return THEME_PREFERENCES.includes(value as ThemePreference)
}

function readStoredTheme(): ThemePreference {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    return isThemePreference(stored) ? stored : DEFAULT_THEME
  } catch {
    // Intentionally ignored: private-mode storage throws.
    return DEFAULT_THEME
  }
}

/** The app's colour theme; a device preference stored only in this browser, applied via `data-theme` on `<html>`. */
export const useThemeStore = defineStore('theme', () => {
  const theme = ref<ThemePreference>(readStoredTheme())

  const themes = computed(() => THEME_PREFERENCES)

  const schemeQuery = darkSchemeQuery()
  const systemDark = ref(schemeQuery?.matches ?? false)
  schemeQuery?.addEventListener('change', (event) => {
    systemDark.value = event.matches
  })

  const resolvedTheme = computed<ResolvedTheme>(() => {
    if (theme.value !== 'system') return theme.value
    return systemDark.value ? 'dark' : 'light'
  })

  function apply(next: ThemePreference): void {
    theme.value = next

    const root = document.documentElement
    if (next === 'system') root.removeAttribute('data-theme')
    else root.setAttribute('data-theme', next)

    try {
      window.localStorage.setItem(STORAGE_KEY, next)
    } catch {
      // Intentionally ignored: the selection still applies for this session.
    }
  }

  function setTheme(next: string): void {
    if (!isThemePreference(next) || next === theme.value) return
    apply(next)
  }

  function init(): void {
    apply(theme.value)
  }

  return { theme, themes, resolvedTheme, setTheme, init }
})
